import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  UserIcon,
  Building2Icon,
  ShieldIcon,
  Edit3Icon,
  SaveIcon,
  XIcon,
  ActivityIcon,
  LockIcon,
  CameraIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeIcon,
  InfoIcon,
  BellIcon,
  HardDriveIcon,
} from 'lucide-react'
import { useState, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  ResponsiveActionRow,
  ResponsivePageHeader,
  ResponsiveTabs,
  TwoFactorSettings,
  DadosCadastraisDaOrganizacao,
} from '@nebula/shared/components'
import { ConfirmModal, AlertModal } from '@nebula/shared/components/modals'
import { logsApi, type LogEntry } from '@nebula/shared/services/logsApi'
import { organizationApi } from '@nebula/shared/services/organizationApi'
import { userApi } from '@nebula/shared/services/userApi'
import { useAuthStore } from '@nebula/shared/store'

const VALID_TABS = ['profile', 'company', 'security', 'activity', 'preferences'] as const
type TabId = (typeof VALID_TABS)[number]

const AccountPage = () => {
  const { user, updateUser } = useAuthStore()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // URL-based tab navigation
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: TabId = VALID_TABS.includes(searchParams.get('tab') as TabId)
    ? (searchParams.get('tab') as TabId)
    : 'profile'
  const setActiveTab = useCallback(
    (tab: TabId) => {
      setSearchParams({ tab }, { replace: true })
    },
    [setSearchParams],
  )

  // Edit state
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
  })

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  })
  const [showPasswordForm, setShowPasswordForm] = useState(false)

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
    variant?: 'danger' | 'warning' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    variant?: 'success' | 'error' | 'warning' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  })

  // Queries
  const { data: orgDetails } = useQuery({
    queryKey: ['organization', user?.organization_id],
    queryFn: () => organizationApi.getById(user!.organization_id),
    enabled: !!user?.organization_id,
  })

  const { data: activityLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['user-activity', user?.id],
    queryFn: () =>
      logsApi.getOrganizationLogs({
        user_id: user!.id,
        limit: 20,
      }),
    enabled: !!user?.id && activeTab === 'activity',
  })

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: (data: { full_name?: string; email?: string }) => userApi.updateCurrentUser(data),
    onSuccess: (updatedUser) => {
      updateUser({ ...user!, full_name: updatedUser.full_name, email: updatedUser.email })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      setIsEditing(false)
      setAlertModal({
        isOpen: true,
        title: 'Perfil atualizado',
        message: 'Suas informações foram salvas com sucesso.',
        variant: 'success',
      })
    },
    onError: () => {
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'Não foi possível atualizar o perfil. Tente novamente.',
        variant: 'error',
      })
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: (password: string) => userApi.updateCurrentUser({ password }),
    onSuccess: () => {
      setPasswordForm({ newPassword: '', confirmPassword: '' })
      setShowPasswordForm(false)
      setAlertModal({
        isOpen: true,
        title: 'Senha alterada',
        message: 'Sua senha foi alterada com sucesso.',
        variant: 'success',
      })
    },
    onError: () => {
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'Não foi possível alterar a senha. Tente novamente.',
        variant: 'error',
      })
    },
  })

  const uploadPhotoMutation = useMutation({
    mutationFn: (file: File) => userApi.uploadProfilePhoto(file),
    onSuccess: (updatedUser) => {
      updateUser({ ...user!, profile_photo_url: updatedUser.profile_photo_url })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: () => {
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'Não foi possível enviar a foto. Tente novamente.',
        variant: 'error',
      })
    },
  })

  const deletePhotoMutation = useMutation({
    mutationFn: () => userApi.deleteProfilePhoto(),
    onSuccess: () => {
      updateUser({ ...user!, profile_photo_url: null })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })

  // Permissão de admin da organização (para exibir o toggle de obrigatoriedade de 2FA)
  const canManageOrg = !!(user?.is_superuser || user?.permissions?.includes('organizations:update'))

  // Handlers
  const handleSave = () => {
    updateProfileMutation.mutate(editForm)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditForm({
      full_name: user?.full_name || '',
      email: user?.email || '',
    })
  }

  const handleStartEdit = () => {
    setEditForm({
      full_name: user?.full_name || '',
      email: user?.email || '',
    })
    setIsEditing(true)
  }

  const handleChangePassword = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'As senhas não coincidem.',
        variant: 'error',
      })
      return
    }
    if (passwordForm.newPassword.length < 8) {
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'A senha deve ter pelo menos 8 caracteres.',
        variant: 'error',
      })
      return
    }
    changePasswordMutation.mutate(passwordForm.newPassword)
  }

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setAlertModal({
          isOpen: true,
          title: 'Erro',
          message: 'A imagem deve ter no máximo 5MB.',
          variant: 'error',
        })
        return
      }
      uploadPhotoMutation.mutate(file)
    }
  }

  const handleDeletePhoto = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Remover foto',
      message: 'Tem certeza que deseja remover sua foto de perfil?',
      variant: 'warning',
      onConfirm: () => deletePhotoMutation.mutate(),
    })
  }

  // Helpers
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateShort = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  const getOrgTypeLabel = (type?: string) => {
    switch (type) {
      case 'cloud_provider':
        return 'Cloud Provider'
      case 'reseller':
        return 'Revenda'
      case 'end_customer':
        return 'Cliente Final'
      case 'system':
        return 'Sistema'
      default:
        return type || '—'
    }
  }

  const getActivityIcon = (source: string) => {
    switch (source) {
      case 'auth':
        return <LockIcon className="h-4 w-4 text-blue-500" />
      case 'user':
        return <UserIcon className="h-4 w-4 text-green-500" />
      case 'rbac':
        return <ShieldIcon className="h-4 w-4 text-purple-500" />
      case 'security':
        return <ShieldIcon className="h-4 w-4 text-red-500" />
      case 'organization':
        return <Building2Icon className="h-4 w-4 text-orange-500" />
      case 'block_storage':
        return <HardDriveIcon className="h-4 w-4 text-indigo-500" />
      default:
        return <ActivityIcon className="h-4 w-4 text-gray-500" />
    }
  }

  const getLogLevelBadge = (level: string) => {
    switch (level) {
      case 'info':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />
      case 'warning':
        return <AlertTriangleIcon className="h-4 w-4 text-yellow-500" />
      case 'error':
      case 'critical':
        return <AlertTriangleIcon className="h-4 w-4 text-red-500" />
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />
    }
  }

  const tabs: { id: TabId; label: string; icon: typeof UserIcon }[] = [
    { id: 'profile', label: 'Perfil', icon: UserIcon },
    { id: 'company', label: 'Empresa', icon: Building2Icon },
    { id: 'security', label: 'Segurança', icon: ShieldIcon },
    { id: 'activity', label: 'Atividade', icon: ActivityIcon },
    { id: 'preferences', label: 'Preferências', icon: GlobeIcon },
  ]

  // ── Tab renders ──

  const renderProfileTab = () => (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex items-center space-x-6">
        <div className="relative">
          {user?.profile_photo_url ? (
            <img
              src={user.profile_photo_url}
              alt={user.full_name}
              className="h-24 w-24 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-purple-100">
              <UserIcon className="h-12 w-12 text-purple-600" />
            </div>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute right-0 bottom-0 rounded-full bg-purple-600 p-1.5 text-white hover:bg-purple-700"
            disabled={uploadPhotoMutation.isPending}
          >
            <CameraIcon className="h-3 w-3" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoUpload}
          />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900">{user?.full_name}</h3>
          <p className="text-sm text-gray-500">{user?.email}</p>
          {user?.organization && <p className="text-sm text-gray-500">{user.organization.name}</p>}
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-purple-600 hover:text-purple-800"
              disabled={uploadPhotoMutation.isPending}
            >
              {uploadPhotoMutation.isPending ? 'Enviando...' : 'Alterar foto'}
            </button>
            {user?.profile_photo_url && (
              <button
                onClick={handleDeletePhoto}
                className="text-sm text-red-500 hover:text-red-700"
                disabled={deletePhotoMutation.isPending}
              >
                Remover
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Nome Completo</label>
          {isEditing ? (
            <input
              type="text"
              value={editForm.full_name}
              onChange={(e) => setEditForm((prev) => ({ ...prev, full_name: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
            />
          ) : (
            <p className="text-gray-900">{user?.full_name || '—'}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
          {isEditing ? (
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
            />
          ) : (
            <p className="text-gray-900">{user?.email || '—'}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Organização</label>
          <p className="text-gray-900">{user?.organization?.name || '—'}</p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
          <p className="text-gray-900">{getOrgTypeLabel(user?.organization?.type)}</p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Funções</label>
          <div className="flex flex-wrap gap-1">
            {user?.roles && user.roles.length > 0 ? (
              user.roles.map((role: any, i: number) => (
                <span key={i} className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800">
                  {typeof role === 'string' ? role : role.name}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-400">Nenhuma função atribuída</span>
            )}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Membro desde</label>
          <p className="text-gray-900">
            {user?.created_at ? formatDateShort(user.created_at) : '—'}
          </p>
        </div>
      </div>

      {/* Memberships */}
      {user?.memberships && user.memberships.length > 1 && (
        <div>
          <h3 className="mb-4 text-lg font-medium text-gray-900">Organizações</h3>
          <div className="space-y-3">
            {user.memberships.map((m, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
              >
                <div>
                  <h4 className="font-medium text-gray-900">{m.organization_name}</h4>
                  <p className="text-sm text-gray-500">{getOrgTypeLabel(m.organization_type)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {m.roles.map((r, j) => (
                    <span
                      key={j}
                      className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800"
                    >
                      {r}
                    </span>
                  ))}
                  {m.is_default && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                      Padrão
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  const renderCompanyTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Nome da Organização
          </label>
          <p className="text-gray-900">{orgDetails?.name || user?.organization?.name || '—'}</p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
          <p className="text-gray-900">
            {getOrgTypeLabel(orgDetails?.type || user?.organization?.type)}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${
              orgDetails?.status === 'active'
                ? 'bg-green-100 text-green-800'
                : orgDetails?.status === 'suspended'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
            }`}
          >
            {orgDetails?.status === 'active'
              ? 'Ativa'
              : orgDetails?.status === 'suspended'
                ? 'Suspensa'
                : orgDetails?.status || '—'}
          </span>
        </div>

        {orgDetails?.description && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
            <p className="text-gray-900">{orgDetails.description}</p>
          </div>
        )}

        {user?.organization?.parent_name && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Vinculada a</label>
            <p className="text-gray-900">{user.organization.parent_name}</p>
          </div>
        )}

        {orgDetails?.frontend_url && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">URL do Portal</label>
            <p className="text-gray-900">{orgDetails.frontend_url}</p>
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Criada em</label>
          <p className="text-gray-900">
            {orgDetails?.created_at ? formatDateShort(orgDetails.created_at) : '—'}
          </p>
        </div>

        {orgDetails?.users_count !== undefined && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Membros</label>
            <p className="text-gray-900">{orgDetails.users_count}</p>
          </div>
        )}
      </div>

      {/* Dados cadastrais (#1014): pelo contrato tipado da API, com fallback ao settings legado */}
      <DadosCadastraisDaOrganizacao
        dadosCadastrais={orgDetails?.dados_cadastrais}
        settings={orgDetails?.settings}
      />
    </div>
  )

  const renderSecurityTab = () => (
    <div className="space-y-6">
      {/* 2FA: app autenticador (TOTP) + e-mail fallback + dispositivos + política da org */}
      <TwoFactorSettings accent="purple" showOrgEnforcement={canManageOrg} />

      <div>
        <h3 className="mb-4 text-lg font-medium text-gray-900">Alterar Senha</h3>
        {!showPasswordForm ? (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
            <div>
              <h4 className="font-medium text-gray-900">Senha da conta</h4>
              <p className="text-sm text-gray-500">
                Última atualização: {user?.updated_at ? formatDate(user.updated_at) : '—'}
              </p>
            </div>
            <button
              onClick={() => setShowPasswordForm(true)}
              className="rounded-lg bg-gray-600 px-4 py-2 text-sm text-white hover:bg-gray-700"
            >
              Alterar
            </button>
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Nova senha</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                }
                placeholder="Mínimo 8 caracteres"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Confirmar nova senha
              </label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                }
                placeholder="Repita a nova senha"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleChangePassword}
                disabled={changePasswordMutation.isPending}
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {changePasswordMutation.isPending ? 'Salvando...' : 'Salvar nova senha'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordForm(false)
                  setPasswordForm({ newPassword: '', confirmPassword: '' })
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderActivityTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-lg font-medium text-gray-900">Log de Atividade</h3>
        <p className="text-sm text-gray-500">Histórico das suas ações na plataforma</p>
      </div>

      {logsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600" />
        </div>
      ) : activityLogs.length === 0 ? (
        <div className="py-12 text-center">
          <ActivityIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <p className="text-gray-500">Nenhuma atividade registrada ainda.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activityLogs.map((log: LogEntry) => (
            <div
              key={log.id}
              className="flex items-start space-x-4 rounded-lg border border-gray-200 p-4"
            >
              <div className="mt-1 flex-shrink-0">{getActivityIcon(log.source)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className="text-sm font-medium text-gray-900">{log.action}</h4>
                  {getLogLevelBadge(log.level)}
                </div>
                <p className="mt-1 text-sm text-gray-600">{log.message}</p>
                {log.ip_address && (
                  <p className="mt-1 text-xs text-gray-400">IP: {log.ip_address}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">{formatDate(log.created_at)}</p>
              </div>
              <div className="flex-shrink-0">
                <span
                  className={`rounded-full px-2 py-1 text-xs ${
                    log.source === 'auth'
                      ? 'bg-blue-50 text-blue-700'
                      : log.source === 'user'
                        ? 'bg-green-50 text-green-700'
                        : log.source === 'security'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  {log.source}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  // Backup notification preference state
  const [backupPref, setBackupPref] = useState<string>(
    user?.backup_notification_preference || 'all',
  )
  const [backupPrefDirty, setBackupPrefDirty] = useState(false)

  const saveBackupPrefMutation = useMutation({
    mutationFn: (pref: string) =>
      userApi.updateCurrentUser({ backup_notification_preference: pref }),
    onSuccess: (updatedUser) => {
      updateUser({
        ...user!,
        backup_notification_preference: updatedUser.backup_notification_preference,
      })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      setBackupPrefDirty(false)
      setAlertModal({
        isOpen: true,
        title: 'Preferência salva',
        message: 'Sua preferência de notificação de backup foi atualizada.',
        variant: 'success',
      })
    },
    onError: () => {
      setAlertModal({
        isOpen: true,
        title: 'Erro',
        message: 'Não foi possível salvar a preferência. Tente novamente.',
        variant: 'error',
      })
    },
  })

  const renderPreferencesTab = () => (
    <div className="space-y-6">
      {/* Backup Notifications */}
      <div>
        <h3 className="mb-1 text-lg font-medium text-gray-900">Notificações de Backup</h3>
        <p className="mb-4 text-sm text-gray-500">
          Escolha quais eventos de backup geram notificações para você.
        </p>

        <div className="space-y-3">
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
              backupPref === 'all'
                ? 'border-purple-300 bg-purple-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="backup_pref"
              value="all"
              checked={backupPref === 'all'}
              onChange={() => {
                setBackupPref('all')
                setBackupPrefDirty(true)
              }}
              className="mt-1 text-purple-600 focus:ring-purple-500"
            />
            <div>
              <span className="font-medium text-gray-900">Todos os eventos</span>
              <p className="mt-0.5 text-sm text-gray-500">
                Backup concluído, backup falho, restauração concluída e restauração falha.
              </p>
            </div>
          </label>

          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
              backupPref === 'failures_only'
                ? 'border-purple-300 bg-purple-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="backup_pref"
              value="failures_only"
              checked={backupPref === 'failures_only'}
              onChange={() => {
                setBackupPref('failures_only')
                setBackupPrefDirty(true)
              }}
              className="mt-1 text-purple-600 focus:ring-purple-500"
            />
            <div>
              <span className="font-medium text-gray-900">Apenas falhas</span>
              <p className="mt-0.5 text-sm text-gray-500">
                Somente backup falho e restauração falha.
              </p>
            </div>
          </label>
        </div>

        <div className="mt-4">
          <button
            onClick={() => saveBackupPrefMutation.mutate(backupPref)}
            disabled={!backupPrefDirty || saveBackupPrefMutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
          >
            <BellIcon className="h-4 w-4" />
            {saveBackupPrefMutation.isPending ? 'Salvando...' : 'Salvar preferência'}
          </button>
        </div>
      </div>

      {/* Other preferences — coming soon */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start space-x-3">
          <InfoIcon className="mt-0.5 h-5 w-5 text-blue-600" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">Em breve</h4>
            <p className="mt-1 text-sm text-blue-700">
              Configurações de idioma, fuso horário e moeda estarão disponíveis em uma próxima
              atualização.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 opacity-60 md:grid-cols-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Idioma</label>
          <select
            disabled
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2"
          >
            <option value="pt-BR">Português (Brasil)</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Fuso Horário</label>
          <select
            disabled
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2"
          >
            <option value="America/Sao_Paulo">São Paulo (UTC-3)</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Moeda</label>
          <select
            disabled
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2"
          >
            <option value="BRL">Real (R$)</option>
          </select>
        </div>
      </div>
    </div>
  )

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Minha Conta"
        description="Gerencie suas informações pessoais e preferências"
        icon={UserIcon}
        actions={
          <ResponsiveActionRow justify="end" className="w-full gap-2 sm:w-auto sm:gap-2">
            {activeTab === 'profile' &&
              (!isEditing ? (
                <button
                  onClick={handleStartEdit}
                  className="flex w-full items-center justify-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 sm:w-auto"
                >
                  <Edit3Icon className="h-4 w-4" />
                  <span>Editar</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={updateProfileMutation.isPending}
                    className="flex w-full items-center justify-center space-x-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50 sm:w-auto"
                  >
                    <SaveIcon className="h-4 w-4" />
                    <span>{updateProfileMutation.isPending ? 'Salvando...' : 'Salvar'}</span>
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 sm:w-auto"
                  >
                    <XIcon className="h-4 w-4" />
                    <span>Cancelar</span>
                  </button>
                </>
              ))}
          </ResponsiveActionRow>
        }
      />

      {/* Tabs */}
      <div className="mb-6">
        <ResponsiveTabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Abas da conta"
        />
      </div>

      {/* Content */}
      <div className="rounded-lg bg-white shadow">
        <div className="p-6">
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'company' && renderCompanyTab()}
          {activeTab === 'security' && renderSecurityTab()}
          {activeTab === 'activity' && renderActivityTab()}
          {activeTab === 'preferences' && renderPreferencesTab()}
        </div>
      </div>

      {/* Modais */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
      />
    </div>
  )
}

export default AccountPage
